/**
 * Metrics mode of `/content` — the three chart sections (SPEC `Content Performance` § Metrics mode,
 * endpoint #87).
 *
 * Each section is `/view`'s Overview island: a joined stat block whose cells toggle series, over one
 * chart, under an outside header carrying the section's own tooltip. The frame is `SectionCard`, so
 * `state:'empty'` paints the server's `reason` and nothing here decides why.
 *
 * The three rules this screen exists to honour, and where each lives:
 *
 *  - **No invented data.** A series the server marked `{available:false}` is *absent from the
 *    chart* and its stat cell states why in the server's words — never a row of zeros.
 *    `contentSeriesPlan`/`plottedContentSeries` make that decision; this file only paints it (see
 *    `StatCell` for the two shapes that sentence takes). Bucket values of `null` already gap in
 *    `Chart`.
 *  - **LinkedIn's own flags are surfaced.** A `partial` bucket rides `ChartSeries.partial` into the
 *    hatched mark and adds "Partial week" to the hover.
 *  - **Nothing is computed here.** Every figure is an `Explained`, every tooltip is the server's,
 *    the LinkedIn totals row is rendered entry by entry, and the in/out chart receives percentages
 *    *and* their rendered strings — the component turns shares into column heights and no more.
 *
 * Four things the refinement fixed, worth not undoing:
 *
 *  - **One chart per section.** Post Performance's profile views used to sit in a sub-block with a
 *    heading and a chart of its own; it is per-post attribution over the section's *own* buckets,
 *    so it is another line, and a second axis-less chart under the first only split the reading.
 *    Its tile keeps everything it said (conversion rate inline, avg/median viewers per post).
 *
 *  - **The stat block is the legend.** A `ChartLegend` under a chart whose colours already sit on
 *    the tiles above it repeated itself, so there is none — on the section charts or on the in/out
 *    block. A tile's tint *is* the series' colour.
 *  - **A tile is lit only when it is plotted**, `plottedContentSeries(...)` and not `active`: at
 *    Daily the profile-views series does not exist, and a solid blue tile driving no line lied.
 *  - **Impressions are the subject, so they take the right axis** (`axis: 'right'`), which carries
 *    the bar family's headroom rule — as lines they simply get a normal `niceMax` and stop
 *    rendering in the bottom quarter under a 4×-inflated scale.
 */

import { useState, type CSSProperties, type ReactNode } from 'react';
import type {
  ContentCommentPerformanceData,
  ContentEstimatedTileDTO,
  ContentLinkedinTotalsDTO,
  ContentMetricsResponse,
  ContentOverviewData,
  ContentPostPerformanceData,
  Explained,
  Tooltip,
} from '@lw/shared';
import {
  Chart,
  StackedShareChart,
  TooltipPopover,
  cx,
  type ChartSeries,
} from '../../components';
import { formatInteger } from '../../lib/format';
import { SectionCard } from '../view/metrics/SectionCard';
import { ContentAuthorsTable } from './ContentAuthorsTable';
import {
  CONTENT_PARTIAL_LABEL,
  DEFAULT_CONTENT_AUTHORS_SORT,
  contentSeriesPlan,
  nextContentAuthorsSort,
  plottedContentSeries,
  toggleContentSeries,
  type ContentAuthorsSort,
  type ContentSeriesPlan,
} from './contentState';
import '../view/metrics/metrics.css';

const CHART_HEIGHT = 300;

/** In/out shares — two hues that read as a split, not as a ranking. */
const IN_NETWORK_COLOR = 'var(--series-reactions)';
const OUT_NETWORK_COLOR = 'var(--series-saves)';

/** Impressions, the subject of every chart on this screen. */
const TOTAL_IMPRESSIONS_COLOR = 'var(--series-saves)';
const REPORTED_IMPRESSIONS_COLOR = 'var(--series-shares)';

type SeriesDef<K extends string> = {
  key: K;
  label: string;
  /** Stat-tile tint and plotted colour — one value, because the tile *is* the legend. */
  color: string;
  type: 'bar' | 'line';
  /** Fill under the line, for a series read as a volume rather than as a trend. */
  area?: boolean;
  /** `'right'` puts the series on the bar-family scale (see the header). */
  axis?: 'left' | 'right';
};

/* ========================================================================== */
/*  Shared pieces                                                             */
/* ========================================================================== */

/**
 * One stat cell of a joined stat block: a toggle for its series plus a corner (i).
 *
 * The two "cannot be shown" shapes the spec distinguishes, and both are the server's words:
 *
 *  - **`reasonDisplay`** — the stat itself came with a sentence, which *replaces* the figures. This
 *    is profile views at Daily/Monthly: LinkedIn reports no resolution that nests in the grid, so
 *    there is no total to state, only the sentence ("switch to Weekly").
 *  - **`unavailableDisplay`** — only the *series* is unavailable, so the figure stands (the server's
 *    own `"—"`) and the caveat rides under it. This is a metric no import ever carried.
 *
 * Either way the cell stops being a toggle: there is no line to plot. `rate` rides inline beside
 * the number rather than as a sub-line — a conversion rate is a property of the figure next to it,
 * and the sub-line belongs to whatever the tile has to say about its own sample.
 */
function StatCell({
  label,
  color,
  active,
  toggleable,
  onToggle,
  value,
  rate,
  sub,
  className,
  reasonDisplay,
  unavailableDisplay,
  tooltip,
  extraTooltip,
}: {
  label: string;
  color: string;
  active: boolean;
  toggleable: boolean;
  onToggle: () => void;
  value: Explained;
  /** Rendered small and muted immediately after `value`, with no separator glyph. */
  rate?: Explained;
  sub?: string;
  className?: string;
  reasonDisplay?: string;
  unavailableDisplay?: string;
  tooltip?: Tooltip;
  /** Appended as a further tooltip section, e.g. the conversion rate's own formula. */
  extraTooltip?: { heading: string; lines: string[] };
}) {
  const body = (
    <>
      <span className="ov-stat__label">{label}</span>
      {reasonDisplay === undefined ? (
        <>
          <span className="ov-stat__value">
            {value.display}
            {rate === undefined ? null : <span className="cn-rate">{rate.display}</span>}
          </span>
          {unavailableDisplay !== undefined ? (
            <span className="cn-stat__reason">{unavailableDisplay}</span>
          ) : sub === undefined ? null : (
            <span className="ov-stat__sub">{sub}</span>
          )}
        </>
      ) : (
        <span className="cn-stat__reason">{reasonDisplay}</span>
      )}
    </>
  );

  return (
    <div
      className={cx('ov-stat', active && 'is-active', !toggleable && 'cn-stat--inert', className)}
      style={{ '--stat-color': color } as CSSProperties}
    >
      {toggleable ? (
        <button type="button" className="ov-stat__btn" aria-pressed={active} onClick={onToggle}>
          {body}
        </button>
      ) : (
        <div className="ov-stat__btn cn-stat__static">{body}</div>
      )}
      {tooltip === undefined ? null : (
        <TooltipPopover
          tooltip={tooltip}
          align="left"
          label={`How ${label} is calculated`}
          extra={
            extraTooltip === undefined || extraTooltip.lines.length === 0 ? undefined : (
              <span className="tip__section">
                <span className="tip__heading">{extraTooltip.heading}</span>
                {extraTooltip.lines.map((line) => (
                  <span className="ov-stat__formula" key={line}>
                    {line}
                  </span>
                ))}
              </span>
            )
          }
        />
      )}
    </div>
  );
}

/** The conversion rate's formula, appended to its tile's (i). Absent when the server sent none. */
function rateExtra(rate: Explained): { heading: string; lines: string[] } | undefined {
  const formula = rate.tooltip?.formula;
  return formula === undefined ? undefined : { heading: 'Conversion rate', lines: [formula] };
}

type BucketLike = { label: string; fullLabel: string };

/**
 * The section's chart. Plots exactly the keys the user turned on *and* the server can supply, in
 * grid order, and hands `Chart` each series' `partial` flags and axis untouched. No legend: the
 * stat tiles above carry the same labels in the same colours.
 */
function SectionChart<K extends string>({
  buckets,
  defs,
  plans,
  plotted,
  ariaLabel,
}: {
  buckets: readonly BucketLike[];
  defs: readonly SeriesDef<K>[];
  plans: Readonly<Record<K, ContentSeriesPlan>>;
  /** Already resolved by the island, so tile and chart cannot disagree about what is plotted. */
  plotted: readonly K[];
  ariaLabel: string;
}) {
  const plottedDefs = defs.filter((def) => plotted.includes(def.key));

  const chartSeries: ChartSeries[] = plottedDefs.map((def) => {
    const plan = plans[def.key];
    // Narrowing only: `plottedContentSeries` already dropped every unavailable key.
    const values = plan.available ? plan.values : [];
    const partial = plan.available ? plan.partial : null;
    return {
      key: def.key,
      type: def.type,
      color: def.color,
      values,
      ...(def.area === true ? { area: true } : {}),
      ...(def.axis === undefined ? {} : { axis: def.axis }),
      ...(partial === null ? {} : { partial }),
    };
  });

  const renderTooltip = (index: number): ReactNode => {
    const bucket = buckets[index];
    if (bucket === undefined) return null;
    const isPartial = plottedDefs.some((def) => {
      const plan = plans[def.key];
      return plan.available && plan.partial?.[index] === true;
    });
    return (
      <div>
        {/* The bucket's real span, spelled by the server ("25 – 31 Aug 2026"). */}
        <div className="t-label">{bucket.fullLabel}</div>
        {plottedDefs.map((def) => {
          const plan = plans[def.key];
          const value = plan.available ? plan.values[index] : null;
          if (value === null || value === undefined) return null;
          return (
            <div className="t-row" key={def.key}>
              <span className="t-label">
                <span className="t-dot" style={{ background: def.color }} aria-hidden />
                {def.label}
              </span>
              <span className="t-value">{formatInteger(value)}</span>
            </div>
          );
        })}
        {isPartial ? <div className="t-hint">{CONTENT_PARTIAL_LABEL}</div> : null}
      </div>
    );
  };

  // A grid with no buckets says nothing here: whether *that* is worth a sentence is the section's
  // `state`/`reason`, which is the server's call (§12.5 rule 5). Only the client's own state — no
  // cell toggled on — earns client copy.
  if (buckets.length === 0) return null;
  if (chartSeries.length === 0) {
    return <p className="ov-chart faint cn-chart__idle">Pick a stat above to plot it.</p>;
  }

  return (
    <div className="ov-chart">
      <Chart
        labels={buckets.map((bucket) => bucket.label)}
        series={chartSeries}
        height={CHART_HEIGHT}
        renderTooltip={renderTooltip}
        ariaLabel={ariaLabel}
      />
    </div>
  );
}

/**
 * LinkedIn's own aggregate block, rendered entry by entry. Not merged, not recomputed, not even
 * re-labelled: heading, labels and values are all the server's (SPEC § Overview).
 */
function LinkedinTotalsRow({ totals }: { totals: ContentLinkedinTotalsDTO }) {
  return (
    <div className="cn-totals">
      <div className="cn-totals__head">
        <span>{totals.headingDisplay}</span>
        <TooltipPopover tooltip={totals.tooltip} align="left" label="What these totals are" />
      </div>
      <div className="cn-totals__list">
        {totals.entries.map((entry) => (
          <span className="cn-total" key={entry.key}>
            <span className="cn-total__label">{entry.label}</span>
            <span className="cn-total__value">{entry.display}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * A subtraction stat, marked estimated by the DTO's own discriminant rather than by sniffing the
 * display string (SPEC decision 11). Its label already says "(estimated)" and the face already
 * carries the `~`, both the server's; the dashed edge is what the class adds. A `StatCell` rather
 * than a `StatCard` so the whole row is one markup and one row height.
 */
function EstimatedTile({ tile }: { tile: ContentEstimatedTileDTO }) {
  return (
    <StatCell
      label={tile.label}
      color="var(--series-total)"
      className="cn-est"
      active={false}
      toggleable={false}
      onToggle={() => undefined}
      value={tile.value}
      rate={tile.conversionRate}
      tooltip={tile.value.tooltip}
      extraTooltip={rateExtra(tile.conversionRate)}
    />
  );
}

/* ========================================================================== */
/*  1. Overview                                                               */
/* ========================================================================== */

type OverviewKey = 'totalImpressions' | 'impressions' | 'profileViews' | 'followers';

const OVERVIEW_SERIES: readonly SeriesDef<OverviewKey>[] = [
  {
    key: 'totalImpressions',
    label: 'Total impressions',
    color: TOTAL_IMPRESSIONS_COLOR,
    type: 'line',
    area: true,
    axis: 'right',
  },
  {
    key: 'impressions',
    label: 'Impressions (report)',
    color: REPORTED_IMPRESSIONS_COLOR,
    type: 'line',
    area: true,
    axis: 'right',
  },
  { key: 'profileViews', label: 'Profile views', color: IN_NETWORK_COLOR, type: 'line' },
  { key: 'followers', label: 'Followers', color: 'var(--series-comments)', type: 'line' },
];

const OVERVIEW_ORDER = OVERVIEW_SERIES.map((def) => def.key);

function OverviewIsland({ data }: { data: ContentOverviewData }) {
  // Both impressions lines and Profile views on; Followers off (SPEC § Overview defaults).
  const [active, setActive] = useState<OverviewKey[]>([
    'totalImpressions',
    'impressions',
    'profileViews',
  ]);
  const plans: Record<OverviewKey, ContentSeriesPlan> = {
    totalImpressions: contentSeriesPlan(data.totalImpressions),
    impressions: contentSeriesPlan(data.impressions),
    profileViews: contentSeriesPlan(data.profileViews),
    followers: contentSeriesPlan(data.followers),
  };
  // One resolution per island: a tile is lit exactly when its series reaches the chart.
  const plotted = plottedContentSeries(active, plans, OVERVIEW_ORDER);

  /** The series' own sentence, shown as a caveat under the figure it could not fill. */
  const unavailableOf = (key: OverviewKey): string | undefined => {
    const plan = plans[key];
    return plan.available ? undefined : plan.reasonDisplay;
  };

  const toggle = (key: OverviewKey) => setActive((current) => toggleContentSeries(current, key));

  return (
    <div className="card">
      <div className="card__body">
        <div className="cn-stats">
          <StatCell
            label={OVERVIEW_SERIES[0].label}
            color={OVERVIEW_SERIES[0].color}
            active={plotted.includes('totalImpressions')}
            toggleable={plans.totalImpressions.available}
            onToggle={() => toggle('totalImpressions')}
            value={data.stats.totalImpressions.total}
            sub={data.stats.totalImpressions.splitDisplay}
            unavailableDisplay={unavailableOf('totalImpressions')}
            tooltip={data.stats.totalImpressions.total.tooltip}
          />
          <StatCell
            label={OVERVIEW_SERIES[1].label}
            color={OVERVIEW_SERIES[1].color}
            active={plotted.includes('impressions')}
            toggleable={plans.impressions.available}
            onToggle={() => toggle('impressions')}
            value={data.stats.impressions.total}
            unavailableDisplay={unavailableOf('impressions')}
            tooltip={data.stats.impressions.total.tooltip}
          />
          <StatCell
            label={OVERVIEW_SERIES[2].label}
            color={OVERVIEW_SERIES[2].color}
            active={plotted.includes('profileViews')}
            toggleable={plans.profileViews.available}
            onToggle={() => toggle('profileViews')}
            value={data.stats.profileViews.total}
            rate={data.stats.profileViews.conversionRate}
            // The one stat that can arrive with its figures replaced by a sentence: LinkedIn
            // reports profile views weekly, and a weekly span nests in no other grid.
            reasonDisplay={data.stats.profileViews.reasonDisplay}
            unavailableDisplay={
              data.stats.profileViews.reasonDisplay === undefined
                ? unavailableOf('profileViews')
                : undefined
            }
            tooltip={data.stats.profileViews.total.tooltip}
            extraTooltip={rateExtra(data.stats.profileViews.conversionRate)}
          />
          <StatCell
            label={OVERVIEW_SERIES[3].label}
            color={OVERVIEW_SERIES[3].color}
            active={plotted.includes('followers')}
            toggleable={plans.followers.available}
            onToggle={() => toggle('followers')}
            value={data.stats.followers.total}
            rate={data.stats.followers.conversionRate}
            unavailableDisplay={unavailableOf('followers')}
            tooltip={data.stats.followers.total.tooltip}
            extraTooltip={rateExtra(data.stats.followers.conversionRate)}
          />
        </div>

        <SectionChart
          buckets={data.buckets}
          defs={OVERVIEW_SERIES}
          plans={plans}
          plotted={plotted}
          ariaLabel="Impressions, profile views and followers gained per bucket"
        />

        {data.linkedinTotals === null ? null : <LinkedinTotalsRow totals={data.linkedinTotals} />}
      </div>
    </div>
  );
}

/* ========================================================================== */
/*  2. Post Performance                                                       */
/* ========================================================================== */

type PostKey = 'impressions' | 'profileViews' | 'followers' | 'engagements';

/**
 * Profile views sits second, right after the impressions it converts from — the same order
 * Overview uses, and the reason its conversion rate reads as a property of the tile beside it.
 * Unlike Overview's, this section's profile-views series is per-post attribution over the *same*
 * `data.buckets`, so it nests in any grid and needs no `reasonDisplay` branch.
 */
const POST_SERIES: readonly SeriesDef<PostKey>[] = [
  {
    key: 'impressions',
    label: 'Impressions',
    color: TOTAL_IMPRESSIONS_COLOR,
    type: 'line',
    area: true,
    axis: 'right',
  },
  { key: 'profileViews', label: 'Profile views', color: IN_NETWORK_COLOR, type: 'line' },
  { key: 'followers', label: 'Followers', color: 'var(--series-comments)', type: 'line' },
  { key: 'engagements', label: 'Engagements', color: 'var(--series-total)', type: 'line' },
];

const POST_ORDER = POST_SERIES.map((def) => def.key);

function PostPerformanceIsland({ data }: { data: ContentPostPerformanceData }) {
  const [active, setActive] = useState<PostKey[]>(['impressions', 'profileViews']);
  const plans: Record<PostKey, ContentSeriesPlan> = {
    impressions: contentSeriesPlan(data.impressions),
    profileViews: contentSeriesPlan(data.profileViews),
    followers: contentSeriesPlan(data.followers),
    engagements: contentSeriesPlan(data.engagements),
  };
  const plotted = plottedContentSeries(active, plans, POST_ORDER);

  const toggle = (key: PostKey) => setActive((current) => toggleContentSeries(current, key));
  const unavailableOf = (key: PostKey): string | undefined => {
    const plan = plans[key];
    return plan.available ? undefined : plan.reasonDisplay;
  };

  return (
    <div className="card">
      <div className="card__body">
        <div className="cn-stats">
          <StatCell
            label={POST_SERIES[0].label}
            color={POST_SERIES[0].color}
            active={plotted.includes('impressions')}
            toggleable={plans.impressions.available}
            onToggle={() => toggle('impressions')}
            value={data.stats.impressions.total}
            sub={`${data.stats.impressions.posts.display} posts · ${data.stats.impressions.median.display} median`}
            unavailableDisplay={unavailableOf('impressions')}
            tooltip={data.stats.impressions.total.tooltip}
            extraTooltip={
              data.stats.impressions.median.tooltip === undefined
                ? undefined
                : {
                    heading: 'Median per post',
                    lines: [data.stats.impressions.median.tooltip.formula],
                  }
            }
          />
          <StatCell
            label={POST_SERIES[1].label}
            color={POST_SERIES[1].color}
            active={plotted.includes('profileViews')}
            toggleable={plans.profileViews.available}
            onToggle={() => toggle('profileViews')}
            value={data.stats.profileViews.total}
            rate={data.stats.profileViews.conversionRate}
            sub={`${data.stats.profileViews.avg.display} avg / ${data.stats.profileViews.median.display} med`}
            unavailableDisplay={unavailableOf('profileViews')}
            tooltip={data.stats.profileViews.total.tooltip}
            extraTooltip={rateExtra(data.stats.profileViews.conversionRate)}
          />
          <StatCell
            label={POST_SERIES[2].label}
            color={POST_SERIES[2].color}
            active={plotted.includes('followers')}
            toggleable={plans.followers.available}
            onToggle={() => toggle('followers')}
            value={data.stats.followers.total}
            rate={data.stats.followers.conversionRate}
            unavailableDisplay={unavailableOf('followers')}
            tooltip={data.stats.followers.total.tooltip}
            extraTooltip={rateExtra(data.stats.followers.conversionRate)}
          />
          <StatCell
            label={POST_SERIES[3].label}
            color={POST_SERIES[3].color}
            active={plotted.includes('engagements')}
            toggleable={plans.engagements.available}
            onToggle={() => toggle('engagements')}
            value={data.stats.engagements.total}
            unavailableDisplay={unavailableOf('engagements')}
            tooltip={data.stats.engagements.total.tooltip}
          />
        </div>

        <SectionChart
          buckets={data.buckets}
          defs={POST_SERIES}
          plans={plans}
          plotted={plotted}
          ariaLabel="Post lifetime metrics and attributed profile viewers grouped by publish date"
        />

        <div className="cn-part">
          <h3 className="metrics-parttitle">
            In vs Out of network
            <TooltipPopover tooltip={data.inOutTooltip} label="How the in/out split is computed" />
          </h3>
          {data.inOutSeries.length === 0 ? null : (
            <StackedShareChart
              buckets={data.inOutSeries}
              inLabel="In network"
              outLabel="Out of network"
              inColor={IN_NETWORK_COLOR}
              outColor={OUT_NETWORK_COLOR}
              ariaLabel="Share of impressions from inside versus outside your network"
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ========================================================================== */
/*  3. Comments Performance                                                   */
/* ========================================================================== */

type CommentKey = 'impressions';

const COMMENT_SERIES: readonly SeriesDef<CommentKey>[] = [
  {
    key: 'impressions',
    label: 'Impressions',
    color: TOTAL_IMPRESSIONS_COLOR,
    type: 'line',
    area: true,
    axis: 'right',
  },
];

const COMMENT_ORDER = COMMENT_SERIES.map((def) => def.key);

function CommentPerformanceIsland({ data }: { data: ContentCommentPerformanceData }) {
  const [active, setActive] = useState<CommentKey[]>(['impressions']);
  const [authorsSort, setAuthorsSort] = useState<ContentAuthorsSort>(DEFAULT_CONTENT_AUTHORS_SORT);
  const plans: Record<CommentKey, ContentSeriesPlan> = {
    impressions: contentSeriesPlan(data.impressions),
  };
  const plotted = plottedContentSeries(active, plans, COMMENT_ORDER);

  return (
    <div className="card">
      <div className="card__body">
        <div className="cn-stats">
          <StatCell
            label={COMMENT_SERIES[0].label}
            color={COMMENT_SERIES[0].color}
            active={plotted.includes('impressions')}
            toggleable={plans.impressions.available}
            onToggle={() => setActive((current) => toggleContentSeries(current, 'impressions'))}
            value={data.stats.impressions.total}
            sub={`${data.stats.impressions.comments.display} comments · ${data.stats.impressions.median.display} median`}
            unavailableDisplay={
              plans.impressions.available ? undefined : plans.impressions.reasonDisplay
            }
            tooltip={data.stats.impressions.total.tooltip}
          />
          <EstimatedTile tile={data.stats.profileViewsEstimated} />
          <EstimatedTile tile={data.stats.followersEstimated} />
        </div>

        <SectionChart
          buckets={data.buckets}
          defs={COMMENT_SERIES}
          plans={plans}
          plotted={plotted}
          ariaLabel="Comment impressions grouped by comment date"
        />

        {data.authors.rows.length === 0 ? null : (
          <div className="cn-part">
            <h3 className="metrics-parttitle">
              Accounts you commented on
              <TooltipPopover
                tooltip={data.authors.tooltip}
                label="How these accounts are grouped"
              />
            </h3>
            <ContentAuthorsTable
              rows={data.authors.rows}
              sort={authorsSort}
              onSort={(key) => setAuthorsSort((current) => nextContentAuthorsSort(current, key))}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/* ========================================================================== */

export type ContentMetricsProps = {
  metrics: ContentMetricsResponse | undefined;
};

export function ContentMetrics({ metrics }: ContentMetricsProps) {
  if (metrics === undefined) return null;

  // `.metrics` carries the section spacing and the island vocabulary this screen reuses.
  return (
    <div className="metrics">
      <SectionCard title="Overview" section={metrics.overview}>
        {(data) => <OverviewIsland data={data} />}
      </SectionCard>

      <SectionCard title="Post Performance" section={metrics.postPerformance}>
        {(data) => <PostPerformanceIsland data={data} />}
      </SectionCard>

      <SectionCard title="Comments Performance" section={metrics.commentPerformance}>
        {(data) => <CommentPerformanceIsland data={data} />}
      </SectionCard>
    </div>
  );
}

export default ContentMetrics;
